import { AVP, Director, LoanOfficerWithContext } from '../types'

type ViewMode = 'avp' | 'director' | 'lo'

function escapeCsv(value: string | number): string {
  const str = String(value)
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`
  return str
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Export rows that mirror the Detailed Performance table for the active view.
 * Columns exactly match what the user sees on screen (no Division column).
 */
export function exportPerformanceCsv(opts: {
  view: ViewMode
  avps: AVP[]
  directors: Director[]
  loanOfficers: LoanOfficerWithContext[]
  filename?: string
}) {
  const { view, avps, directors, loanOfficers, filename } = opts

  if (view === 'avp') {
    const rankMap = new Map<string, number>()
    ;[...avps]
      .sort((a, b) => b.avgRevenue - a.avgRevenue)
      .forEach((a, i) => rankMap.set(a.id, i + 1))

    const headers = [
      'Rank',
      'Leader',
      'LO Count',
      'Loans',
      'Loan Volume',
      'Total Commissions (Wire)',
      'Avg Revenue',
    ]
    const rows = [...avps]
      .sort((a, b) => b.avgRevenue - a.avgRevenue)
      .map((a) => [
        rankMap.get(a.id) ?? '',
        a.name,
        a.loCount,
        a.loansClosed,
        a.loanVolume.toFixed(2),
        a.totalRevenue.toFixed(2),
        a.avgRevenue.toFixed(2),
      ])
    const csv = [headers, ...rows]
      .map((r) => r.map(escapeCsv).join(','))
      .join('\n')
    downloadCsv(csv, filename ?? 'avp-performance.csv')
    return rows.length
  }

  if (view === 'director') {
    const rankMap = new Map<string, number>()
    ;[...directors]
      .sort((a, b) => b.avgRevenue - a.avgRevenue)
      .forEach((d, i) => rankMap.set(d.id, i + 1))

    const headers = [
      'Rank',
      'Director',
      'Reports To',
      'LO Count',
      'Loans',
      'Loan Volume',
      'Total Commissions (Wire)',
      'Avg Revenue',
    ]
    const rows = [...directors]
      .sort((a, b) => b.avgRevenue - a.avgRevenue)
      .map((d) => [
        rankMap.get(d.id) ?? '',
        d.name,
        d.avpName ?? '—',
        d.loCount,
        d.loansClosed,
        d.loanVolume.toFixed(2),
        d.totalRevenue.toFixed(2),
        d.avgRevenue.toFixed(2),
      ])
    const csv = [headers, ...rows]
      .map((r) => r.map(escapeCsv).join(','))
      .join('\n')
    downloadCsv(csv, filename ?? 'director-performance.csv')
    return rows.length
  }

  // Loan Officers — ranked by totalRevenue
  const rankMap = new Map<string, number>()
  ;[...loanOfficers]
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .forEach((lo, i) => rankMap.set(lo.id, i + 1))

  const headers = [
    'Rank',
    'Officer',
    'Reports To',
    'Loans',
    'Loan Volume',
    'Total Commissions (Wire)',
    'Avg Revenue',
  ]
  const rows = [...loanOfficers]
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .map((lo) => [
      rankMap.get(lo.id) ?? '',
      lo.name,
      lo.directorName,
      lo.loansClosed,
      lo.loanVolume.toFixed(2),
      lo.totalRevenue.toFixed(2),
      lo.avgRevenue.toFixed(2),
    ])
  const csv = [headers, ...rows]
    .map((r) => r.map(escapeCsv).join(','))
    .join('\n')
  downloadCsv(csv, filename ?? 'loan-officer-performance.csv')
  return rows.length
}
